import React from 'react';
import type {InputBaseComponentProps, InputLabelProps} from '@mui/material';
import {type NumberValue} from '@netx/core-asset/models/attribute';
import {numberTypes} from '@netx/core-asset/properties/attributes';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {NumberField} from '@netx/core-components/NumberField';
import {usePlaceholder} from '../../../../hooks';
import {type IAttributeTemplate} from '../../../../schema';
import type {CommonProps} from '../../types';
import Lookup from '../MetadataLookup/Lookup';

const formatNumber = (attrData: IAttributeTemplate, value: string) => {
	let numberLength = value.length;
	if (!numberLength) return '';

	const numberType = numberTypes[attrData.spec2];
	const maxDecimal = (numberType && numberType.maxDecimal) || Infinity;

	numberLength = value.length;

	if (!numberLength) return '';
	if (!numberType) return value;

	if (numberType.key === 'whole') {
		return value.split('.')[0].substr(0, attrData.spec1 || value.length);
	}

	if (numberType.key === 'unlimited') {
		return value;
	}

	const decimalIndex = value.indexOf('.');
	if (maxDecimal !== Infinity) {
		if (decimalIndex > -1) {
			const decimalDigitCount = numberLength - (decimalIndex + 1);
			if (decimalDigitCount > maxDecimal) {
				value = value.substr(
					0,
					numberLength - (decimalDigitCount - maxDecimal),
				);
			}
		}
	}
	return value;
};

export interface NumberAttributeProps extends CommonProps<NumberValue> {}

export const NumberAttributePrimitive = ({
	action,
	attrData,
	hideLabel,
	isMetadataLookup,
	isMetadataTarget,
	multipleValues,
	placeholder,
	readOnly,
	value,
	onChange,
	onLookup,
	// so it does no get passed along
	// only some of our attribute types are offer folksonomy
	disableFolksonomy,
	// only some of our attribute types are searchable
	onSearch,
	...props
}: NumberAttributeProps) => {
	const cleared = action === 'clear';
	const defaultValue = value == null ? '' : value.toString();
	const maxLength =
		attrData.spec2 === 0 ? attrData.spec1 || undefined : undefined;

	const textfieldPlaceholder = usePlaceholder(attrData, placeholder, {
		cleared,
		multipleValues,
		readOnly,
		value: defaultValue,
	});

	const renderInput = useConstCallback(
		(params?: {
			inputProps: InputBaseComponentProps;
			InputLabelProps: InputLabelProps;
		}) => {
			return (
				<NumberField
					hideErrorMessage
					id={attrData.attributeid.toString()}
					label={hideLabel ? undefined : attrData.name}
					maxLength={maxLength}
					format={(value) => formatNumber(attrData, value)}
					fullWidth
					name={attrData.name}
					{...params}
					{...props}
					placeholder={textfieldPlaceholder}
					value={defaultValue}
					onChange={onChange}
					InputLabelProps={{
						...params?.InputLabelProps,
						shrink: multipleValues || cleared ? true : undefined,
					}}
				/>
			);
		},
	);

	if (isMetadataLookup && onLookup) {
		return (
			<Lookup
				attrData={attrData}
				disabled={props.disabled}
				value={defaultValue.toString()}
				onChange={(value) =>
					onChange?.({target: {value: value ?? ''}} as any, value as string)
				}
				onLookup={onLookup}
				renderInput={renderInput}
			/>
		);
	}

	return renderInput();
};

export const NumberAttribute = React.memo(NumberAttributePrimitive);

export default NumberAttribute;
